import { PageProps, graphql } from 'gatsby';
import AnalyticsPoint from 'gatsby-plugin-purina-analytics/AnalyticsPoint';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Layout from '../../../components/Layout';
import { ButtonLink } from '../../../components/Link';
import Seo from '../../../components/Seo';
import ProductList from '../../../components/product_list/ProductList';
import RichText from '../../../components/rich_text/RichText';
import { BrazeProvider } from '../../../hooks/useBraze/useBraze';
import {
  getHelmetPropsFromMetatagProps,
  getJsonFromMetatagProps,
} from '../../../utils/metatagHelpers';
import BreedTitleSection from './components/BreedTitleSection';
import { useGetBreedCustomInfo } from './hooks/useGetBreedTitle';
import { DataProps } from './types';

const BreedTemplate: React.FC<PageProps<DataProps>> = function ({
  data: { node, translations, allStorageBrazeCard },
}) {
  const helmetProps = getHelmetPropsFromMetatagProps(node.metatag);
  const schemaMarkup = getJsonFromMetatagProps(node.metatag);
  const breedName = node.title;
  const breedCollections = node.relationships?.field_breed_collection;
  const relatedProducts = node.relationships?.relatedProducts;
  const { relatedProductTitle, relatedBreedTitle } = useGetBreedCustomInfo({
    breed: breedName,
    species: node.relationships?.species?.name ?? '',
  });

  const hasRelatedProducts = relatedProducts?.length > 0;

  const renderRelatedProducts = (
    <div className="pds-px-4 pds-py-5.5 md:pds-py-6">
      <div className="pds-mx-auto pds-w-full pds-max-w-site-container pds-space-y-4 sm:pds-space-y-4.5">
        <h2 className="pds-m-0 pds-text-start pds-text-title-sm sm:pds-text-center lg:pds-text-start">
          {relatedProductTitle}
        </h2>
        <BrazeProvider location="breed_detail" allStorageBrazeCard={allStorageBrazeCard}>
          <ProductList
            productsList={relatedProducts}
            onClick={title =>
              sendIt({
                event: 'module_interaction',
                eventParams: {
                  component_id: title,
                  component_type: 'product_card',
                  content_name: 'product_card: breed',
                },
              })
            }
          />
        </BrazeProvider>
      </div>
    </div>
  );

  return (
    <Layout
      node={node}
      metaData={node.metatag}
      language={node.langcode}
      translations={translations}
    >
      <Seo {...helmetProps} schemaMarkup={schemaMarkup} />
      <BreedTitleSection node={node} />
      {node.body?.processed && (
        <Container className="my-5">
          <Row className="justify-content-md-center">
            <Col xs="12" lg="9" xl="8">
              <RichText body={node.body.processed} />
            </Col>
          </Row>
        </Container>
      )}
      {hasRelatedProducts && renderRelatedProducts}
      {breedCollections && breedCollections.length > 0 && (
        <AnalyticsPoint
          type="module"
          label="breed collections"
          typeLabel="breed_collections"
          as={Container}
          className="pds-bg-surface-line pds-py-6"
          fluid
        >
          <div className="pds-mx-auto pds-max-w-site-container">
            <Row>
              <h2
                className="pds-mb-4.5 pds-text-title-lg pds-font-normal [&_strong]:pds-font-extrabold"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: relatedBreedTitle }}
              />
              <ul className="pds-flex pds-flex-wrap pds-items-start pds-gap-3.5 pds-pl-0">
                {breedCollections.map(collection => {
                  const pageDelegatePath =
                    collection.relationships?.field_page_delegate?.path?.alias;
                  if (!pageDelegatePath) return null;
                  const handleAnalytics = () => {
                    sendIt({
                      event: 'module_interaction',
                      eventParams: {
                        component_id: 'bdp: related collections',
                        component_type: 'link_button: explore like breeds',
                        content_name: collection.name,
                      },
                    });
                  };
                  return (
                    <li key={collection.id}>
                      <ButtonLink
                        to={pageDelegatePath}
                        buttonStyle="solid"
                        buttonColor="inverse"
                        className="pds-rounded-pill pds-bg-surface-layer pds-py-3 pds-text-body-md hover:pds-bg-surface focus-visible:pds-outline-focus"
                        onClick={handleAnalytics}
                        analytics={false}
                      >
                        {collection.name}
                      </ButtonLink>
                    </li>
                  );
                })}
              </ul>
            </Row>
          </div>
        </AnalyticsPoint>
      )}
    </Layout>
  );
};

export const query = graphql`
  query ($id: String!, $language: String!, $nid: Int!) {
    node: nodeBreed(id: { eq: $id }, langcode: { eq: $language }) {
      internal {
        type
      }
      title
      langcode
      body {
        processed
      }
      ...Metatag
      behaviors {
        page_layout {
          elements_to_hide {
            header
            footer
            chat
          }
        }
        hide_elements {
          third_party_elements_to_hide {
            one_trust
          }
        }
      }
      field_breed_info {
        activities
        coat
        color
        energy
        height
        size
        indoor_outdoor
        weight
      }
      country_links: other_country_external_links {
        text
        url
        hreflang
      }
      relationships {
        field_breed_collection {
          name
          relationships {
            field_page_delegate {
              path {
                alias
              }
            }
          }
        }
        field_image {
          ...MediaImage
          relationships {
            field_media_image {
              gatsbyImage(aspectRatio: 1, width: 500, placeholder: NONE)
            }
          }
        }
        relatedProducts: field_product {
          ...ProductCard
          ...ProductBundleCard
          ...ProductNonFoodCard
        }
        species: field_species_term {
          name
        }
      }
    }
    allStorageBrazeCard(
      filter: { langcode: { eq: $language }, field_location_region: { in: "breed_detail" } }
      sort: { created: DESC }
    ) {
      edges {
        node {
          ...StorageBrazeCard
        }
      }
    }
    translations: allNodeBreed(
      filter: { drupal_internal__nid: { eq: $nid }, langcode: { ne: $language } }
    ) {
      edges {
        node {
          langcode
          path {
            alias
          }
        }
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

export default BreedTemplate;
